import React, { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Sparkles, Loader2, CheckCircle, ChevronDown, Copy, Check, RefreshCw, Zap } from 'lucide-react';
import { toast } from 'sonner';
import MarkdownContent from './MarkdownContent';
import ReasoningBlock from './ReasoningBlock';
import ThinkingOrb from '../signature/ThinkingOrb';

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('已複製', { duration: 1500 });
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('複製失敗'); }
  };
  return (
    <button
      onClick={handleCopy}
      className="p-1.5 rounded-md hover:bg-white/5 text-obsidian-300 hover:text-white transition-colors"
      data-testid="copy-msg-btn" title="複製"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-lime-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

const MessageBubble = ({ msg, onReload, isLast, loading }) => {
  if (msg.role === 'user') {
    return (
      <div className="group flex justify-end gap-1.5 items-end msg-slide-right">
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 flex-shrink-0">
          <CopyButton text={msg.content} />
        </div>
        <div className="message-user max-w-[min(68ch,720px)]">
          {msg.mentioned_subagent_name ? (
            <span>
              <span className="font-bold italic bg-lime-500/15 text-lime-500 px-1.5 py-0.5 rounded">
                @{msg.mentioned_subagent_name}
              </span>
              {' '}{msg.content.replace(/^@[\w\u4e00-\u9fff\u3400-\u4dbf]+\s*/, '')}
            </span>
          ) : msg.content}
        </div>
      </div>
    );
  }

  const showActions = !msg.isStreaming && !msg.isGreeting && msg.content;

  return (
    <div className="flex justify-start msg-slide-left">
      <div
        className={`max-w-3xl w-full space-y-3 ${msg.subagent_name ? 'pl-3 border-l-[3px] rounded-sm' : ''}`}
        style={msg.subagent_name ? { borderLeftColor: msg.subagent_color || '#FF5436' } : undefined}
      >
        {msg.subagent_name && (
          <div className="flex items-center gap-1.5 mb-1" data-testid={`subagent-label-${msg.subagent_id || ''}`}>
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: msg.subagent_color || '#FF5436' }} />
            <span className="text-xs font-semibold" style={{ color: msg.subagent_color || '#FF5436' }}>{msg.subagent_name}</span>
          </div>
        )}

        {/* Processing with Thinking Orb */}
        {msg.isProcessing && msg.progress && (
          <div className="bg-obsidian-800 border border-obsidian-600 rounded-2xl p-5">
            <div className="flex items-center gap-4">
              <ThinkingOrb size={44} />
              <div className="flex-1">
                <div className="text-white font-medium text-sm mb-2">{msg.progress}</div>
                <div className="label-micro text-obsidian-400">AI · PROCESSING</div>
              </div>
            </div>
          </div>
        )}

        {msg.reasoning && (
          <ReasoningBlock
            reasoning={msg.reasoning}
            isThinking={msg.isThinking}
            autoCollapse={!msg.isThinking && !msg.isStreaming}
          />
        )}

        {msg.isUltraGenerating && (
          <div
            className="bg-obsidian-800 border border-lime-500/30 rounded-2xl p-5"
            style={{ boxShadow: '0 0 24px rgba(255,84,54,0.12)' }}
            data-testid="ultra-mode-animation"
          >
            <div className="flex items-center gap-4">
              <ThinkingOrb size={48} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <Zap className="w-3.5 h-3.5 text-lime-500" fill="currentColor" />
                  <div className="text-white font-semibold text-sm">Ultra 模式運行中</div>
                </div>
                <div className="text-obsidian-200 text-xs">
                  {msg.ultraStatus || msg.ultraProgress || '正在生成高品質內容…'}
                </div>
              </div>
            </div>
          </div>
        )}

        {msg.reviewProgress && (
          <div className="bg-obsidian-800 border border-obsidian-600 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              {msg.isReviewing
                ? <Loader2 className="w-5 h-5 text-lime-500 animate-spin flex-shrink-0" />
                : <CheckCircle className="w-5 h-5 text-lime-500 flex-shrink-0" />}
              <div className="flex-1">
                <div className="text-white font-medium text-sm">{msg.reviewProgress}</div>
                {msg.isReviewing && (
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-lime-500 rounded-full animate-pulse w-1/2" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {msg.reviewFeedback && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4">
            <div className="text-amber-100 text-sm whitespace-pre-wrap">{msg.reviewFeedback}</div>
          </div>
        )}

        {msg.review_history && msg.review_history.length > 0 && !msg.reviewFeedback && (
          <Collapsible defaultOpen={false}>
            <div className="bg-obsidian-800 border border-obsidian-600 rounded-2xl overflow-hidden">
              <CollapsibleTrigger className="w-full p-3.5 flex items-center justify-between hover:bg-white/[0.02] transition-all duration-200">
                <div className="flex items-center gap-2 text-white font-medium text-sm">
                  <Sparkles className="w-3.5 h-3.5 text-lime-500" />
                  <span>Ultra 審核記錄</span>
                  <span className="text-xs bg-lime-500/15 text-lime-500 px-2 py-0.5 rounded-full label-micro">
                    {msg.review_history.length} 次
                  </span>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-obsidian-300" strokeWidth={2} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-4 pb-4 text-obsidian-100 text-sm border-t border-obsidian-600 pt-3 space-y-3">
                  {msg.review_history.map((review, idx) => (
                    <div key={idx} className="bg-obsidian-900 rounded-xl p-3 border border-obsidian-600">
                      <div className="flex items-center gap-2 mb-2">
                        {review.passed
                          ? <CheckCircle className="w-3.5 h-3.5 text-lime-500" />
                          : <span className="w-3.5 h-3.5 rounded-full bg-red-500 flex items-center justify-center text-[10px] text-white">✕</span>}
                        <span className="font-medium text-white">第 {review.attempt} 次審核</span>
                        <span className="label-micro bg-white/5 text-obsidian-200 px-2 py-0.5 rounded-full">
                          評分：{review.score}/10
                        </span>
                      </div>
                      {review.issues?.length > 0 && (
                        <div className="text-xs text-obsidian-200 mt-1">
                          <span className="font-medium">問題：</span>{review.issues.join(', ')}
                        </div>
                      )}
                      {review.suggestions?.length > 0 && (
                        <div className="text-xs text-obsidian-300 mt-1">
                          <span className="font-medium">建議：</span>{review.suggestions.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        )}

        {msg.wasOptimized && !msg.isStreaming && (
          <div className="flex items-center gap-1.5 label-micro text-lime-500">
            <Sparkles className="w-3 h-3" />
            <span>ULTRA · OPTIMIZED</span>
          </div>
        )}

        {(msg.content || !msg.reasoning) && (
          <div className="group/bubble">
            <div
              className={`${msg.subagent_name ? 'message-subagent' : 'message-assistant'} ${msg.isStreaming ? 'streaming-reveal' : ''} max-w-[min(72ch,820px)]`}
              style={msg.subagent_name ? { '--sa-color': msg.subagent_color || '#FF5436' } : undefined}
            >
              <MarkdownContent content={msg.content} />
              {msg.isStreaming && !msg.isThinking && !msg.isProcessing && (
                <span className="stream-cursor" aria-hidden />
              )}
            </div>
            {showActions && (
              <div className="flex items-center gap-0.5 mt-2 opacity-0 group-hover/bubble:opacity-100 transition-opacity md:opacity-0 md:group-hover/bubble:opacity-100" data-testid="msg-actions">
                <CopyButton text={msg.content} />
                {onReload && !loading && (
                  <button
                    onClick={onReload}
                    className="p-1.5 rounded-md hover:bg-white/5 text-obsidian-300 hover:text-white transition-colors"
                    data-testid="reload-msg-btn" title="從這裡重新生成"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
